import { lightTheme } from '@zenbusiness/zui/styles';

/**
 * Check if the fields are valid and should show an error message
 * @param {object} fields - object containing the fields to check
 * @returns {object} - object with the same keys as the input object, but with boolean values
 * indicating if the field should show an error message
 */
export function checkShowErrors(fields) {
  if (!fields) {
    return {};
  }

  const result = {};
  for (const [field, fieldData] of Object.entries(fields)) {
    result[field] = Boolean(!fieldData.isValid && fieldData.isTouched && !fieldData.isFocused);
  }
  return result;
}

/**
 * Check if the fields related to the card are focused
 * @param {object} fields - object containing the fields to check
 * @returns {boolean} - true if any of the card fields are focused, false otherwise
 */
export function checkIsForcusCardFields(fields) {
  if (!fields) {
    return false;
  }

  return Boolean(
    fields?.expiration_date?.isFocused ||
      fields?.card_security_code?.isFocused ||
      fields?.card_number?.isFocused
  );
}

/**
 * Check if the card fields are invalid and should show an error message
 * @param {object} fields - object containing the fields to check
 * @returns {boolean} - true if any of the card fields are invalid, false otherwise
 */
export function CheckIsShowErrorsCardFields(fields) {
  if (!fields) {
    return false;
  }

  //   get only card fields
  const cardFields = {
    card_number: fields.card_number,
    expiration_date: fields.expiration_date,
    card_security_code: fields.card_security_code
  };

  //   use reduce to check if any of the card fields are invalid
  return Object.values(cardFields).reduce(
    (acc, field) => acc || Boolean(!field?.isValid && field?.isTouched),
    false
  );
}

/**
 * Get the border color for the card fields
 * @param {object} fields - object containing the fields to check
 * @returns {string} - the color to use for the border of the card fields
 */
export function getBorderColorForCardFields(fields) {
  if (checkIsForcusCardFields(fields)) {
    return lightTheme.palette.border.main;
  }

  if (CheckIsShowErrorsCardFields(fields)) {
    return lightTheme.palette.error.main;
  }

  return lightTheme.palette.border.main;
}

/**
 * Get the error message for the card number field
 * @param {object} fields - object containing the fields to check
 * @returns {string} - the error message to show for the card number field
 */
export function getErrorMessageForCardNumber(fields) {
  const cardType = fields?.card_number?.cardType;

  if (cardType && cardType !== 'visa' && cardType !== 'mastercard') {
    return 'Must be Visa® or Mastercard®';
  }

  return 'Please enter a valid 16-digit card number';
}

/**
 * Check if all fields are valid
 * @param {object} fields - object containing the fields to check
 * @returns {boolean} - true if all fields are valid, false otherwise
 */
export function isAllFieldsValid(fields) {
  if (!fields) {
    return false;
  }

  return Object.values(fields).every((field) => field.isValid);
}

/**
 * Get the first error message from an error object
 * @param {object} errorObject - object containing the error messages
 * @returns {string} - the first error message in the object
 */
export function getFirstDescriptionErrorMessage(errorObject) {
  if (!errorObject?.description) {
    return null;
  }

  if (typeof errorObject.description === 'string') {
    return errorObject.description;
  }

  const errorMessages = Object.values(errorObject.description);
  for (const messages of errorMessages) {
    if (Array.isArray(messages) && messages.length > 0) {
      return messages[0];
    }
  }

  return null;
}
